/**
 * PALLET packing: full pallets first (pallet dims), then leftover masters after them.
 * Tuning knobs are local so GRANEL stays independent.
 */

import { chooseOrientation } from "./orientation";
import { orientationCapacity } from "./types";
import {
  packSingleContainer,
  packAll,
  maxZExtent,
  DEFAULT_MIN_BELOW_FRACTION,
} from "./steppedPacker";

/** Local fill-line fraction for pallet units (can diverge from GRANEL). */
const PALLET_MIN_BELOW = DEFAULT_MIN_BELOW_FRACTION;
/** Local fill-line fraction for leftover masters in the remaining length. */
const MASTER_TAIL_MIN_BELOW = DEFAULT_MIN_BELOW_FRACTION;

/**
 * @param {object} input
 * @param {number} input.masterWidth
 * @param {number} input.masterHeight
 * @param {number} input.masterDepth
 * @param {number} input.palletWidth
 * @param {number} input.palletHeight
 * @param {number} input.palletDepth
 * @param {number} input.mastersPerLayer
 * @param {number} input.layersPerPallet
 * @param {number} input.quantity - number of master cartons
 * @param {number} input.contL
 * @param {number} input.contW
 * @param {number} input.contH
 */
export function packPallet(input) {
  const {
    masterWidth,
    masterHeight,
    masterDepth,
    palletWidth,
    palletHeight,
    palletDepth,
    mastersPerLayer,
    layersPerPallet,
    quantity,
    contL,
    contW,
    contH,
  } = input;

  const masters = Math.max(0, Math.floor(quantity));
  const perPallet = Math.max(
    0,
    Math.floor(mastersPerLayer) * Math.floor(layersPerPallet)
  );

  if (perPallet <= 0) {
    // No pallet config — fall back to loose masters.
    return packAll(
      masterWidth,
      masterHeight,
      masterDepth,
      masters,
      [[contL, contW, contH]],
      "master",
      MASTER_TAIL_MIN_BELOW
    );
  }

  const fullPallets = Math.floor(masters / perPallet);
  const remainderMasters = masters % perPallet;

  const containers = [];
  let palletsLeft = fullPallets;
  let mastersLeft = remainderMasters;
  let idx = 0;
  let totalCapacity = 0;

  // Always produce at least one container result.
  while (palletsLeft > 0 || mastersLeft > 0 || idx === 0) {
    if (idx > 100) break;

    const palletOrient = chooseOrientation(
      palletWidth,
      palletHeight,
      palletDepth,
      contL,
      contW,
      contH
    );
    const palletCap = orientationCapacity(palletOrient);

    const boxes = [];
    let palletResult = null;
    let takePallets = 0;

    if (palletsLeft > 0 && palletCap > 0) {
      takePallets = Math.min(palletsLeft, palletCap);
      palletResult = packSingleContainer({
        containerIndex: idx,
        contL,
        contW,
        contH,
        orientation: palletOrient,
        quantity: takePallets,
        kind: "pallet",
        minBelowFraction: PALLET_MIN_BELOW,
      });
      boxes.push(...palletResult.boxes);
      palletsLeft -= palletResult.quantity;
    } else if (palletsLeft > 0 && palletCap <= 0) {
      // Pallets do not fit — stop.
      containers.push({
        containerIndex: idx,
        contL,
        contW,
        contH,
        orientation: palletOrient,
        quantity: 0,
        capacity: 0,
        boxes: [],
        columnHeights: [],
        steps: [],
        diagonal: [0, 0, contL, 0],
      });
      break;
    }

    // Remaining length after pallets for leftover masters (same container only
    // when no more full pallets need this container's full capacity spill path).
    const usedZ = maxZExtent(boxes);
    const remainingLength = Math.max(0, contL - usedZ);

    let masterResult = null;
    if (mastersLeft > 0 && remainingLength > 0 && palletsLeft === 0) {
      const masterOrient = chooseOrientation(
        masterWidth,
        masterHeight,
        masterDepth,
        remainingLength,
        contW,
        contH
      );
      const masterCap = orientationCapacity(masterOrient);
      if (masterCap > 0) {
        const takeMasters = Math.min(mastersLeft, masterCap);
        masterResult = packSingleContainer({
          containerIndex: idx,
          contL,
          contW,
          contH,
          orientation: masterOrient,
          quantity: takeMasters,
          kind: "master",
          zOffset: usedZ,
          usableLength: remainingLength,
          minBelowFraction: MASTER_TAIL_MIN_BELOW,
        });
        boxes.push(...masterResult.boxes);
        mastersLeft -= masterResult.quantity;
      }
    }

    // Capacity: pallet slots + any master capacity in this container estimate.
    const capacity =
      (palletResult?.capacity ?? 0) + (masterResult?.capacity ?? 0);
    totalCapacity += capacity;

    containers.push({
      containerIndex: idx,
      contL,
      contW,
      contH,
      orientation: palletResult?.orientation ?? palletOrient,
      quantity: boxes.length,
      capacity,
      boxes,
      columnHeights: palletResult?.columnHeights ?? [],
      steps: palletResult?.steps ?? [],
      diagonal: palletResult?.diagonal ?? [0, 0, contL, 0],
    });

    idx += 1;

    if (palletsLeft <= 0 && mastersLeft <= 0) break;

    // Spill leftover masters that did not fit into remaining length into a new
    // container as loose masters (or more pallets if still pending).
    if (palletsLeft <= 0 && mastersLeft > 0) {
      // Next iteration will open a new container with only masters (no pallets).
      // Handle remaining masters with packAll into subsequent containers.
      const spill = packAll(
        masterWidth,
        masterHeight,
        masterDepth,
        mastersLeft,
        [[contL, contW, contH]],
        "master",
        MASTER_TAIL_MIN_BELOW
      );
      for (const c of spill.containers) {
        containers.push({
          ...c,
          containerIndex: idx,
          boxes: c.boxes.map((b) => ({ ...b, containerIndex: idx })),
        });
        totalCapacity += c.capacity;
        idx += 1;
      }
      mastersLeft = 0;
      break;
    }
  }

  const primary = containers[0]?.orientation ?? null;
  return {
    orientation: primary,
    containers,
    totalQuantity: fullPallets + remainderMasters,
    totalCapacity,
  };
}
